import { debounce, isFunction } from '@agilepoint/utils-common';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useForceUpdate } from '../../../../utils';
import { IItemPositionData, IListViewContentManagerProps, IItemData } from '../types';

function useNormalizeItem(itemHeight: number, itemWidth: number, isHorizontallyAligned: boolean) {
    const getTopPosition = useCallback((index: number, top?: number) => {
        if (isHorizontallyAligned) {
            return 0;
        }
        if (top !== undefined) {
            return top;
        }
        return index * itemHeight;
    }, [isHorizontallyAligned, itemHeight]);

    const getLeftPosition = useCallback((index: number, left?: number) => {
        if (!isHorizontallyAligned) {
            return 0;
        }
        if (left !== undefined) {
            return left;
        }
        return index * itemWidth;
    }, [isHorizontallyAligned, itemWidth]);

    const normalizeItem = (item: any, index: number, itemPosition?: number) => ({
        item,
        position: { height: itemHeight, width: itemWidth, top: getTopPosition(index, itemPosition), left: getLeftPosition(index, itemPosition) }
    });

    return { normalizeItem };
}

function updateItemPosition(args: { avgItemSpace: number, isHorizontallyAligned: boolean, referenceItemInfo?: IItemData, targetItemInfo?: IItemData }) {
    const { avgItemSpace, isHorizontallyAligned, referenceItemInfo, targetItemInfo } = args;
    if (referenceItemInfo === undefined || targetItemInfo === undefined) {
        return;
    }
    const { position: { left, width = avgItemSpace, top, height = avgItemSpace } } = referenceItemInfo;
    const { position } = targetItemInfo;
    if (isHorizontallyAligned) {
        position.left = left + width;
        return;
    }
    position.top = top + height;
}

export function useItemPositionInfo(options: IListViewContentManagerProps) {
    const { items, itemHeight, itemWidth, isHorizontallyAligned, getItemSize } = options;
    const avgItemSpaceRef = useRef<number>(0);
    const itemPositionInfoRef = useRef(new Map<IItemData['item'], IItemData>());
    const forceUpdate = useForceUpdate();
    const isSizePassedThroughtCallback = isFunction(getItemSize);

    const shouldLoadWithConstantSpace = useCallback(() => ((isHorizontallyAligned ? itemWidth : itemHeight) > 0), [isHorizontallyAligned, itemHeight, itemWidth]);

    const [isContentReadyToView, setContentViewState] = useState(false);
    const { normalizeItem } = useNormalizeItem(itemHeight ?? avgItemSpaceRef.current, itemWidth ?? avgItemSpaceRef.current, isHorizontallyAligned);

    const contentReadyToViewRef = useRef(debounce(() => {
        setContentViewState(true);
    }, 10));

    const getOccupiedSpace = useCallback((start: number, end: number = items.length - 1) => {
        const spaceProperty = isHorizontallyAligned ? 'width' : 'height';
        let totalSpace = 0;
        let itemDimension: IItemData | undefined;
        for (let i = start; i <= end; i++) {
            itemDimension = itemPositionInfoRef.current.get(items[i]);
            totalSpace += (
                isSizePassedThroughtCallback
                    ? getItemSize(items[i])
                    : (
                        (itemDimension === undefined || !itemDimension.position[spaceProperty])
                            ? avgItemSpaceRef.current
                            : itemDimension.position[spaceProperty])
            );
        }
        return totalSpace;
    }, [getItemSize, isHorizontallyAligned, isSizePassedThroughtCallback, items]);

    const updateAvgItemSpace = useCallback((renderedItemIndex: number) => {
        avgItemSpaceRef.current = getOccupiedSpace(0, renderedItemIndex) / (renderedItemIndex + 1);
    }, [getOccupiedSpace]);

    const adjustItemsPosition = useMemo(() => debounce(() => {
        const itemPositionInfo = itemPositionInfoRef.current;
        for (let i = 0; i < items.length - 1; i++) {
            updateItemPosition({ avgItemSpace: avgItemSpaceRef.current, isHorizontallyAligned, referenceItemInfo: itemPositionInfo.get(items[i]), targetItemInfo: itemPositionInfo.get(items[i + 1]) });
        }
        contentReadyToViewRef.current();
        forceUpdate();
    }, 100), [forceUpdate, isHorizontallyAligned, items]);

    const onItemRectCapture = useCallback((item: any, positionInfo: IItemPositionData) => {
        if (isSizePassedThroughtCallback) {
            return;
        }
        const itemIndex = items.indexOf(item);
        const itemPositionInfo = itemPositionInfoRef.current;
        if (!itemPositionInfo.has(item)) {
            itemPositionInfo.set(item, normalizeItem(item, itemIndex));
        }
        const itemInfo = itemPositionInfo.get(item);
        if (shouldLoadWithConstantSpace() || !itemInfo) {
            return;
        }
        const startPosition = getOccupiedSpace(0, itemIndex - 1);
        itemInfo.position = {
            height: isHorizontallyAligned ? Math.max(0, itemHeight) : positionInfo.height,
            width: isHorizontallyAligned ? positionInfo.width : Math.max(0, itemWidth),
            top: isHorizontallyAligned ? 0 : startPosition,
            left: isHorizontallyAligned ? startPosition : 0
        };
        updateAvgItemSpace(itemIndex);
        adjustItemsPosition();
    }, [adjustItemsPosition, getOccupiedSpace, isHorizontallyAligned, isSizePassedThroughtCallback, itemHeight, itemWidth, items, normalizeItem, shouldLoadWithConstantSpace, updateAvgItemSpace]);

    const getItemPositionInfo = useCallback((item: IItemData['item']) => itemPositionInfoRef.current.get(item), []);
    const setItemPositionInfo = useCallback((itemInfo: IItemData) => itemPositionInfoRef.current.set(itemInfo.item, itemInfo), []);

    const getAvgItemSpace = useCallback(() => avgItemSpaceRef.current, []);

    const initializeItemsWithConstantDimensions = useCallback((item: any, index: number) => {
        if (itemPositionInfoRef.current.has(item)) {
            return;
        }
        const topPosition = isHorizontallyAligned ? 0 : index * itemHeight;
        const leftPosition = isHorizontallyAligned ? index * itemWidth : 0;
        itemPositionInfoRef.current.set(item, {
            item,
            position: { height: itemHeight, width: itemWidth, top: topPosition, left: leftPosition }
        });
    }, [isHorizontallyAligned, itemHeight, itemWidth]);

    useMemo(() => {
        if (!isSizePassedThroughtCallback) {
            return;
        }
        let item, prevItem, size: number, prevItemInfo: IItemData | undefined;
        const itemPositionInfo = itemPositionInfoRef.current;
        itemPositionInfo.clear();
        for (let index = 0; index < items.length; index++) {
            item = items[index];
            prevItem = index > 0 ? items[index - 1] : null;
            size = getItemSize(item)
            prevItemInfo = itemPositionInfo.get(prevItem);
            itemPositionInfo.set(item, {
                item,
                position: {
                    height: isHorizontallyAligned ? 0 : size,
                    width: isHorizontallyAligned ? size : 0,
                    top: isHorizontallyAligned ? 0 : (prevItemInfo ? prevItemInfo.position.top + prevItemInfo.position.height : 0),
                    left: isHorizontallyAligned ? (prevItemInfo ? prevItemInfo.position.left + prevItemInfo.position.width : 0) : 0
                }
            });
        }
        contentReadyToViewRef.current();
    }, [getItemSize, isHorizontallyAligned, isSizePassedThroughtCallback, items]);

    useMemo(() => {
        if (isSizePassedThroughtCallback) {
            return;
        }
        if (!shouldLoadWithConstantSpace()) {
            if (itemPositionInfoRef.current.size === 0 && items.length) {
                const [item] = items;
                itemPositionInfoRef.current.set(item, normalizeItem(item, 0));
            }
            return;
        }
        contentReadyToViewRef.current();
        itemPositionInfoRef.current.clear();
        items.forEach(initializeItemsWithConstantDimensions);
        updateAvgItemSpace(items.length - 1);
    }, [initializeItemsWithConstantDimensions, isSizePassedThroughtCallback, items, normalizeItem, shouldLoadWithConstantSpace, updateAvgItemSpace]);

    return {
        isContentReadyToView, getItemPositionInfo, shouldLoadWithConstantSpace,
        onItemRectCapture, getOccupiedSpace, getAvgItemSpace,
        normalizeItem, setItemPositionInfo
    };
}
